import { useState, useEffect } from 'react';
import axios from '../lib/axios';
import { Upload, Check, X, Eye, Download } from 'lucide-react';

interface GdprVersion {
  id: string;
  version: string;
  content: string;
  pdfUrl?: string;
  publishedAt: string;
}

interface TenantWithVersions {
  tenant: {
    id: string;
    name: string;
    slug: string;
  };
  versions: GdprVersion[];
}

interface GdprTabProps {
  tenantId?: string;
  isSuperAdmin?: boolean;
}

export default function GdprTab({ tenantId, isSuperAdmin }: GdprTabProps) {
  const [versions, setVersions] = useState<GdprVersion[] | TenantWithVersions[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<GdprVersion | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState({
    version: '',
    content: '',
    pdfFile: null as File | null,
  });
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadVersions();
  }, [tenantId, selectedTenant]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const endpoint = isSuperAdmin
        ? `/platform/gdpr/versions`
        : `/admin/gdpr/${tenantId}/versions`;

      const res = await axios.get(endpoint);
      setVersions(res.data.data || []);
    } catch (err: any) {
      console.error('[GdprTab] Error loading versions:', err);
      setError('Failed to load GDPR versions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!newVersion.version.trim() || !newVersion.content.trim()) {
      setError('Version number and content are required');
      return;
    }

    setCreating(true);
    const formData = new FormData();
    formData.append('version', newVersion.version);
    formData.append('content', newVersion.content);
    if (newVersion.pdfFile) {
      formData.append('pdfFile', newVersion.pdfFile);
    }

    try {
      const endpoint = tenantId
        ? `/admin/gdpr/${tenantId}/versions`
        : `/platform/gdpr/versions`;

      const res = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        setMessage(res.data.data?.message || 'GDPR version created successfully');
        setNewVersion({ version: '', content: '', pdfFile: null });
        
        // Reload versions
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (err: any) {
      const errorMsg = 
        err.response?.data?.error?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to create GDPR version';
      setError(errorMsg);
      console.error('[GdprTab] Error:', err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">GDPR Privacy Policy Versions</h2>
        <p className="text-gray-600 mb-6">Manage privacy policy versions. Each visitor registration saves the GDPR version accepted.</p>
      </div>

      {/* Create New Version */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Version</h3>
        <form onSubmit={handleCreateVersion} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2">
              <X size={16} />
              {error}
            </div>
          )}
          {message && (
            <div className="p-3 bg-green-100 text-green-700 rounded-lg flex items-center gap-2">
              <Check size={16} />
              {message}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Version Number</label>
            <input
              type="text"
              placeholder="e.g., 1.0"
              value={newVersion.version}
              onChange={(e) => setNewVersion({ ...newVersion, version: e.target.value })}
              className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">HTML Content</label>
            <textarea
              placeholder="Enter the full HTML content of the privacy policy..."
              value={newVersion.content}
              onChange={(e) => setNewVersion({ ...newVersion, content: e.target.value })}
              rows={8}
              className="w-full px-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">PDF Document (Optional)</label>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => setNewVersion({ ...newVersion, pdfFile: e.target.files?.[0] || null })}
              className="w-full px-4 py-2 border border-blue-200 rounded-lg"
            />
            {newVersion.pdfFile && (
              <p className="text-sm text-green-600 mt-2">
                ✓ {newVersion.pdfFile.name}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2"
          >
            <Upload size={16} />
            {creating ? 'Creating...' : 'Create Version'}
          </button>
        </form>
      </div>

      {/* Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> All visitor registrations will automatically record which GDPR version was accepted. PDFs are stored for compliance and audit purposes.
        </p>
      </div>

      {/* View Versions Section */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">GDPR Versions History</h3>
          <button
            onClick={() => {
              setShowModal(true);
              loadVersions();
            }}
            className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <Eye size={16} />
            View Versions
          </button>
        </div>
        <p className="text-gray-600 text-sm">View all created GDPR versions</p>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-96 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                {isSuperAdmin ? 'GDPR Versions - All Tenants' : 'GDPR Versions History'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedVersion(null);
                }}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-6 text-center text-gray-600">Loading versions...</div>
              ) : isSuperAdmin ? (
                // SuperAdmin view - show all tenants
                <div className="p-6 space-y-6">
                  {(versions as TenantWithVersions[]).length === 0 ? (
                    <p className="text-center text-gray-600">No GDPR versions found</p>
                  ) : (
                    (versions as TenantWithVersions[]).map((group) => (
                      <div key={group.tenant.id} className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3">{group.tenant.name}</h3>
                        <div className="space-y-2">
                          {group.versions.length === 0 ? (
                            <p className="text-sm text-gray-500">No versions</p>
                          ) : (
                            group.versions.map((v) => (
                              <div
                                key={v.id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <div>
                                  <p className="font-semibold text-gray-900">v{v.version}</p>
                                  <p className="text-xs text-gray-500">
                                    {new Date(v.publishedAt).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setSelectedVersion(v)}
                                    className="px-3 py-1 bg-blue-100 text-blue-600 rounded text-xs font-semibold hover:bg-blue-200"
                                  >
                                    View
                                  </button>
                                  {v.pdfUrl && (
                                    <a
                                      href={v.pdfUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-3 py-1 bg-green-100 text-green-600 rounded text-xs font-semibold hover:bg-green-200 flex items-center gap-1"
                                    >
                                      <Download size={12} />
                                      PDF
                                    </a>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                // Admin view - show only this tenant's versions
                <div className="p-6 space-y-3">
                  {(versions as any)?.versions?.length === 0 ? (
                    <p className="text-center text-gray-600">No GDPR versions found</p>
                  ) : (
                    (versions as any)?.versions?.map((v: GdprVersion) => (
                      <div
                        key={v.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div>
                          <p className="font-semibold text-gray-900">v{v.version}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(v.publishedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedVersion(v)}
                            className="px-3 py-1 bg-blue-100 text-blue-600 rounded text-xs font-semibold hover:bg-blue-200"
                          >
                            View
                          </button>
                          {v.pdfUrl && (
                            <a
                              href={v.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-1 bg-green-100 text-green-600 rounded text-xs font-semibold hover:bg-green-200 flex items-center gap-1"
                            >
                              <Download size={12} />
                              PDF
                            </a>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Version Detail Modal */}
          {selectedVersion && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedVersion(null)}
            >
              <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-96 overflow-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4 flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Version {selectedVersion.version}</h3>
                  <button
                    onClick={() => setSelectedVersion(null)}
                    className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-4">
                    Published: {new Date(selectedVersion.publishedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                  <div
                    className="prose prose-sm max-w-none bg-gray-50 p-4 rounded-lg text-sm"
                    dangerouslySetInnerHTML={{ __html: selectedVersion.content }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
